import { todayPT } from "@homebound/activesupport";
import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import type { ResponsiveProps } from "@nivo/core";
import { Point, ResponsiveLine, SliceTooltipProps, type LineSvgProps } from "@nivo/line";
import { Fragment, useMemo } from "react";

/**
 * The series shape our charts hand to nivo: a date or date string on x, and a number on y, either of which
 * may be missing for a gap in the line.
 *
 * nivo 0.99 made the line types generic over this, where 0.80 hard-coded one permissive datum type. Naming it
 * once here keeps the parameter off every prop below.
 */
export type ChartSeries = { id: string; data: readonly { x: string | Date | null; y: number | null }[] };

// `ResponsiveLine` measures its own box, so it takes the chart props without `width`/`height`.
type Props = ResponsiveProps<LineSvgProps<ChartSeries>>;

type LineChartProps = {
  dataset: Props["data"];
  colors?: Palette[];
  loadingColor?: Palette[];
  disabled?: boolean;
  tooltipValueFormat?: (points: readonly Point<ChartSeries>[]) => string;
  emptyStateTooltipText?: string;
} & Omit<Props, "data" | "colors" | "isInteractive">;

export function LineChart(props: LineChartProps) {
  const { dataset, colors, loadingColor, disabled, tooltipValueFormat, emptyStateTooltipText, ...otherProps } = props;
  const tid = useTestIds({});
  const Chart = useMemo(
    () => (
      <ResponsiveLine
        data={dataset}
        colors={disabled ? loadingColor : colors}
        isInteractive={!disabled}
        sliceTooltip={tooltipValueFormat ? customTooltip(tooltipValueFormat) : undefined}
        enableGridX={false}
        enablePoints={false}
        theme={{ axis: { ticks: { text: { fontSize: 10, fill: Palette.Gray700 } } } }}
        {...otherProps}
      />
    ),
    [dataset, colors, loadingColor, disabled, tooltipValueFormat, otherProps],
  );

  return emptyStateTooltipText ? (
    <Tooltip {...tid.dataTooltip} title={emptyStateTooltipText} placement="top" children={Chart} />
  ) : (
    <Fragment children={Chart} />
  );
}

function customTooltip(tooltipValueFormat: (points: readonly Point<ChartSeries>[]) => string) {
  return ({ slice }: SliceTooltipProps<ChartSeries>) => (
    <div css={Css.bgGray900.white.xs.df.mt1.p1.br4.bshBasic.maxwPx(300).tal.$}>{tooltipValueFormat(slice.points)}</div>
  );
}

export function generateEmptyDataset(yMin: number, yMax: number): ChartSeries[] {
  const today = todayPT();
  return [
    {
      id: "empty_dataset",
      data: [today, today.subtract({ years: 1 })].map((x, i) => ({ x: x.toString(), y: i === 0 ? yMin : yMax })), // Use full range of x/y axis
    },
  ];
}
